import { useEffect, useState } from 'react'
import { format, parseISO } from 'date-fns'
import { CheckCircle2, Mail, XCircle } from 'lucide-react'
import { BoardCard, CardMessage, addCardEvent, fetchCardMessages, mailtoLink, setCardMessageStatus } from '../../lib/boards'

interface CardEmailsPanelProps {
  card: BoardCard
  actorEmail: string | null
  onCountsChanged: () => void
}

export function CardEmailsPanel({ card, actorEmail, onCountsChanged }: CardEmailsPanelProps) {
  const [messages, setMessages] = useState<CardMessage[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setError(null)
    fetchCardMessages(card.id).then(setMessages).catch(e => console.error('Could not load messages (non-fatal):', e))
  }, [card.id, card.columnId])

  const setMessageStatus = async (m: CardMessage, status: 'sent' | 'cancelled') => {
    try {
      const updated = await setCardMessageStatus(m.id, status)
      setMessages(prev => prev.map(x => (x.id === m.id ? updated : x)))
      await addCardEvent(card.id, 'log', status === 'sent' ? `Email sent to ${m.toAddress}` : `Email to ${m.toAddress} cancelled`, actorEmail)
      onCountsChanged()
    } catch (e: any) {
      setError(e?.message || 'Could not update the email.')
    }
  }

  if (messages.length === 0) return null

  return (
    <div className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Family emails</h3>

      <ul className="space-y-2">
        {messages.map(m => (
          <li
            key={m.id}
            className={`p-3 rounded-lg border text-sm ${
              m.status === 'queued' ? 'border-amber-400/60 bg-amber-500/5' : 'border-gray-200 dark:border-gray-700 opacity-70'
            }`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                <Mail className="h-4 w-4 text-amber-500" />
                {m.status === 'queued' ? 'Email to send' : m.status === 'sent' ? 'Email sent' : 'Email cancelled'}
                <span className="font-normal text-gray-500 dark:text-gray-400">to {m.toAddress}</span>
              </p>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {format(parseISO(m.sentAt ?? m.createdAt), 'MMM d, h:mm a')}
              </span>
            </div>
            <details className="mt-1">
              <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer">{m.subject ?? 'Message'}</summary>
              <pre className="mt-2 whitespace-pre-wrap font-sans text-xs text-gray-700 dark:text-gray-300">{m.body}</pre>
            </details>
            {m.status === 'queued' && (
              <div className="flex flex-wrap gap-2 mt-2">
                <a
                  href={mailtoLink(m)}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-brand-600 text-white hover:bg-brand-800"
                >
                  <Mail className="h-3.5 w-3.5" /> Open in email
                </a>
                <button
                  onClick={() => setMessageStatus(m, 'sent')}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-md text-green-600 border border-green-500/40 hover:bg-green-500/10"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Mark sent
                </button>
                <button
                  onClick={() => setMessageStatus(m, 'cancelled')}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-md text-gray-500 hover:text-red-500"
                >
                  <XCircle className="h-3.5 w-3.5" /> Don't send
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  )
}
